import { themeColors } from '../themeColors';
import BackButton from "@/components/Common/BackButton";
/**
 * @file   src\pages\InventoryAllSentIntroductions.js
 * @brief  This file is responsible for handling inventry wise sent introductions list.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import _ from "lodash";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { pageURLs } from "@/constants";
import {
  getMyAcceptedIntroductionByInvId,
  getMyPendingIntroductionByInvId,
  cancelIntroduction,
  getIntroductionWantAdDetailsByIntroId,
} from "../services/introductionService";
import ViewWantAdModal from "../components/Introduction/ViewWantAdModal";
import { toast } from "react-toastify";
import AgentProfileViewModal from "../components/Introduction/AgentProfileViewModal";
import { updateIntroduction } from "../helpers/message";
import PendingSendIntroductionList from "../components/Introduction/PendingSendIntroductionList";
import AcceptedSendIntroductionList from "../components/Introduction/AcceptedSendIntroductionList";
import { PageWrapper } from '@/components/PageWrapper';
import IntroStatusLine from "@/components/IntroStatusLine";
import { getSentIntroductions } from "@/services/closeOutLoopService";

const PENDING_INTROS_KEY = "inventorySentIntroductionsPending";
const STATUS_PAGE_CAP = 20;
const ACCEPTED_INTROS_KEY = "inventorySentIntroductionsAccepted";
const INTRO_LIST_PAGE_LIMIT = 4;

const InventoryAllSentIntroductions = (props) => {

  const navigate = useNavigate();
  const location = useLocation();
  const [showViewWantAdModal, setViewWantAdModal] = useState(false);
  const ViewWantAdModalClose = () => setViewWantAdModal(false);

  const [showProfileModal, setProfileModal] = useState(false);
  const ProfileModalClose = () => setProfileModal(false);

  const [selectedUserId, setSelectedUserId] = useState(0);
  const [wantAdViewInventoryId, setWantAdViewInventoryId] = useState(0);

  const [showAcceptIntroductionModal, setAcceptIntroductionModal] =
    useState(false);
  const AcceptIntroductionModalShow = () => setAcceptIntroductionModal(true);
  const pendingScrollSection = "pending";
  const acceptedScrollSection = "accepted";
  const [searchParams] = useSearchParams();
  const inventoryId = location.state?.inventoryId || Number(searchParams.get("inventoryId")) || null;
  const title = location.state?.title || searchParams.get("title") || "";

  const [wantAdDetails, setWantAdDetails] = useState([]);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!inventoryId) {
      navigate(pageURLs.home);
    }
  }, [inventoryId]);

  /**
   * Shared fetcher for a page of introductions. Throws on API error so React
   * Query surfaces it through the query's error state.
   * @param {function} fetcher service call (pending or accepted)
   * @param {number} page
   */
  const fetchIntroductionsPage = async (fetcher, page) => {
    const response = await fetcher({
      inventoryId: inventoryId,
      page: page,
      limit: INTRO_LIST_PAGE_LIMIT,
    });
    if (response?.errorCode !== 0) {
      throw new Error(response?.message || "Failed to fetch introductions");
    }
    return {
      list: _.get(response, "result.List", []) || [],
      count: _.get(response, "result.Count", 0),
      page,
    };
  };

  const getNextPageParam = (lastPage, pages) => {
    const loaded = pages.reduce((sum, p) => sum + p.list.length, 0);
    return loaded < lastPage.count ? lastPage.page + 1 : undefined;
  };

  const pendingQuery = useInfiniteQuery({
    queryKey: [PENDING_INTROS_KEY, inventoryId],
    queryFn: ({ pageParam = 1 }) =>
      fetchIntroductionsPage(getMyPendingIntroductionByInvId, pageParam),
    getNextPageParam,
    enabled: !!inventoryId,
  });

  const acceptedQuery = useInfiniteQuery({
    queryKey: [ACCEPTED_INTROS_KEY, inventoryId],
    queryFn: ({ pageParam = 1 }) =>
      fetchIntroductionsPage(getMyAcceptedIntroductionByInvId, pageParam),
    getNextPageParam,
    enabled: !!inventoryId,
  });

  const sentStatusQuery = useInfiniteQuery({
    queryKey: ["sentIntroductionStatus", inventoryId],
    queryFn: ({ pageParam }) =>
      getSentIntroductions({ inventoryId, limit: 50, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last, pages) =>
      last?.hasNextPage && pages.length < STATUS_PAGE_CAP
        ? pages.length + 1
        : undefined,
    enabled: !!inventoryId,
  });
  const {
    hasNextPage: moreStatuses,
    isFetchingNextPage: fetchingStatuses,
    fetchNextPage: fetchMoreStatuses,
  } = sentStatusQuery;
  useEffect(() => {
    if (moreStatuses && !fetchingStatuses) fetchMoreStatuses();
  }, [moreStatuses, fetchingStatuses, fetchMoreStatuses]);
  const sentItems = useMemo(
    () => (sentStatusQuery.data?.pages ?? []).flatMap((p) => p?.items ?? []),
    [sentStatusQuery.data],
  );
  const statusById = useMemo(
    () =>
      Object.fromEntries(sentItems.map((i) => [i.introductionId, i.status])),
    [sentItems],
  );
  const answeredElsewhere = useMemo(
    () =>
      sentItems.filter((i) =>
        [
          "not_a_fit",
          "search_found_home",
          "search_ended",
          "withdrawn",
        ].includes(i.status.key),
      ),
    [sentItems],
  );

  // An introduction already answered or ended is listed under "Answered or
  // ended"; the pending query still returns those on a closed Want-Listing.
  const pendingList = useMemo(() => {
    const ended = new Set(answeredElsewhere.map((i) => i.introductionId));
    return (pendingQuery.data?.pages ?? [])
      .flatMap((p) => p.list)
      .filter((intro) => !ended.has(intro.IntroductionId));
  }, [pendingQuery.data, answeredElsewhere]);
  const acceptedList = useMemo(
    () => (acceptedQuery.data?.pages ?? []).flatMap((p) => p.list),
    [acceptedQuery.data]
  );

  const updateIntroPageOnScroll = (section) => {
    if (section === pendingScrollSection) {
      if (pendingQuery.hasNextPage && !pendingQuery.isFetchingNextPage) {
        pendingQuery.fetchNextPage();
      }
    } else if (section === acceptedScrollSection) {
      if (acceptedQuery.hasNextPage && !acceptedQuery.isFetchingNextPage) {
        acceptedQuery.fetchNextPage();
      }
    }
  };

  /**
   * To handle cancel introducion
   * @param {*} introductionId
   */
  const onClickCancelIntroduction = async (introductionId) => {
    const reqPayload = {
      introductionId: introductionId,
    };
    await cancelIntroduction(reqPayload).then(async (response) => {
      if (response) {
        if (response.errorCode == 0) {
          let data = {
            isdeleted: true,
          };

          let updateresponse = await updateIntroduction(introductionId, data);

          toast.success(response.message);
          queryClient.setQueryData(
            [PENDING_INTROS_KEY, inventoryId],
            (old) =>
              old && {
                ...old,
                pages: old.pages.map((p) => ({
                  ...p,
                  list: p.list.filter(
                    (lst) => lst.IntroductionId !== introductionId
                  ),
                })),
              }
          );
        } else {
          console.log(`####error`);
        }
      }
    });
  };
  /**
   * To handle view want ad
   * @param {*} introductionId
   */
  const ViewWantAd = async (introductionId) => {
    setWantAdViewInventoryId(introductionId);
    const reqPayload = {
      introductionId: introductionId,
    };
    await getIntroductionWantAdDetailsByIntroId(reqPayload).then((response) => {
      if (response) {
        if (response.errorCode == 0) {
          setWantAdDetails(response.result);
          setViewWantAdModal(true);
        } else {
          console.log(`####error`);
        }
      }
    });
  };

  const ViewProfile = (agentId) => {
    setViewWantAdModal(false);
    setSelectedUserId(agentId);
    setProfileModal(true);
  };

  const closeAgentModal = () => {
    ViewWantAd(wantAdViewInventoryId);
    setProfileModal(false);
  };

  return (
    <>

      <PageWrapper>
        <div className="m-0">
          <div className="main-content p-0 agent-no-login-content">
            <div className="row m-0">
              <div className="col-md-12">
                <div className="edit-want-ad-wrap introduction-wrap mw-697">
                  <BackButton text="Back" onClick={() => navigate(`${pageURLs.inventoryDetails}/${inventoryId}`)} />
                  <h3>
                    All introductions{title ? <> – <span>{title}</span></> : null}
                  </h3>
                  <div className="payments-wrap intro-content-block">
                    <PendingSendIntroductionList
                      list={pendingList}
                      count={pendingList.length}
                      acceptedCount={acceptedList.length}
                      updateIntroPageOnScroll={updateIntroPageOnScroll}
                      statusById={statusById}
                      CancelIntroduction={(introductionId) => {
                        onClickCancelIntroduction(introductionId);
                      }}
                      ViewWantAd={(id) => {
                        ViewWantAd(id);
                      }}
                    />
                    <AcceptedSendIntroductionList
                      list={acceptedList}
                      count={acceptedList.length}
                      updateIntroPageOnScroll={updateIntroPageOnScroll}
                      AcceptIntroductionModalShow={AcceptIntroductionModalShow}
                      statusById={statusById}
                      ViewWantAd={(id) => {
                        ViewWantAd(id);
                      }}
                    />
                    {answeredElsewhere.length > 0 && (
                      <div className="intro-list-sec">
                        <div className="title-sec">
                          <h5>
                            Answered or ended{" "}
                            <span>({answeredElsewhere.length})</span>
                          </h5>
                        </div>
                        {answeredElsewhere.map((item) => (
                          <div className="property-block" key={item.introductionId}>
                            <div className="property-info">
                              <p className="mb-0">{item.wantAdTitle}</p>
                              <IntroStatusLine status={item.status} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>

      <ViewWantAdModal
        show={showViewWantAdModal}
        onHide={ViewWantAdModalClose}
        wantAdDetails={wantAdDetails}
        ViewProfileModal={(agentId) => {
          ViewProfile(agentId);
        }}
      />

      <AgentProfileViewModal
        show={showProfileModal}
        onHide={ProfileModalClose}
        userId={selectedUserId}
        onClose={() => {
          closeAgentModal();
        }}
      />
    </>
  );
};

export default InventoryAllSentIntroductions;
